import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import { getToken } from 'grab-login/lib/helper';
import { isPaxUser, getPaxAccessToken } from 'helper/grab-id-auth-helper';
import { getCompanyID } from 'helper/user';
import { getEnvVar } from 'helper/env-helper';
import { userInfoSelector } from 'selectors/app-selector';
import { isEmpty } from 'common/utils/data-utils';
import _get from 'lodash/get';
import {
  STORAGE_USER_COUNTRY_CODE,
  STORAGE_LOCALE,
} from 'constants/app-constants';
import { getDefaultErrorContext } from 'common/utils/rum-utils';

export const fetchWithTimeout = async (input, init = {}) => {
  const { timeout = 5000 } = init;
  // eslint-disable-next-line no-undef
  const abortController = new AbortController(); // this is included in modern browsers
  const id = setTimeout(() => abortController.abort(), timeout);
  const response = await fetch(input, {
    ...init,
    signal: abortController.signal,
  });
  clearTimeout(id);
  return response;
};

// Should not send additional headers for external APIs
const EXTERNAL_API_ACTIONS = [
  'GET_USERNAME_INFO_START',
  'GFB_FETCH_CITY_START',
];
const MTS_SSID_HEADER_EXCLUDED_ACTIONS = ['GFB_FETCH_CITY_START'];

const shouldExcludeInternalAPIHeaders = action => {
  if (!action) return true;
  const { start } = action;
  return start && EXTERNAL_API_ACTIONS.includes(start().type);
};

const includeInternalAPIHeaders = action =>
  !shouldExcludeInternalAPIHeaders(action);

const shouldExcludeMTSHeader = action => {
  if (!action) return true;
  const { start } = action;
  return start && MTS_SSID_HEADER_EXCLUDED_ACTIONS.includes(start().type);
};

const shouldIncludeMTSHeader = action => !shouldExcludeMTSHeader(action);

export const buildHeaders = (
  authType,
  action,
  companyID,
  isInternalAPI = false,
) => {
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  if (includeInternalAPIHeaders(action) || isInternalAPI) {
    const locale = window.localStorage.getItem(STORAGE_LOCALE);
    const userCountryCode = window.localStorage.getItem(
      STORAGE_USER_COUNTRY_CODE,
    );
    if (!isEmpty(locale)) {
      headers['Accept-Language'] = locale;
    }
    headers['X-Grab-Web-App-Version'] = getEnvVar('APP_VERSION');
    if (!isEmpty(userCountryCode)) {
      headers['X-Country-Code'] = userCountryCode;
    }
    if (!isEmpty(companyID)) {
      headers['X-Company-ID'] = companyID;
    }
  }

  // if auth method is cookie, adding Authorization key (even if value null)  will break api-gw auth
  if (window.Grab.storage.getItem('__AUTH_HINT__') === 'COOKIE') {
    return headers;
  }

  if (!authType) {
    if (isPaxUser()) {
      // grabid authorization
      headers.Authorization = `Bearer ${getPaxAccessToken()}`;
    } else {
      const jwt = getToken();
      headers.Authorization = jwt;
      if (shouldIncludeMTSHeader(action)) {
        headers['X-mts-ssid'] = jwt;
      }
    }
  } else {
    headers[authType.type] = authType.value;
  }
  return headers;
};

export const authorizedFetch = async (url, companyID, isInternalAPI) => {
  try {
    const response = await fetch(url, {
      headers: buildHeaders(null, null, companyID, isInternalAPI),
      credentials:
        window.Grab.storage.getItem('__AUTH_HINT__') === 'COOKIE'
          ? 'include'
          : 'omit',
    });
    if (!response.ok) {
      return null;
    }
    if (response.status === 204) {
      return null;
    }
    return response.json();
  } catch (err) {
    const errorCtx = rumErrorCtx(getDefaultErrorContext())
      .component('authorizedFetch')
      .message(
        `Authorized fetch error: ${url} - ${err.message || 'Unknown error'}`,
      )
      .severity(SEVERITY.HIGH)
      .custom('endpoint', url)
      .custom('companyID', companyID)
      .custom('isInternalAPI', isInternalAPI)
      .build();
    ddRum.addError(err, errorCtx);
    throw err;
  }
};

const fetchHelper = (endpoint, action, option = {}, callback, authType) => (
  dispatch,
  getState,
) => {
  const { start, success, error } = action;
  if (start) {
    dispatch(start(true));
  }

  const userInfo = userInfoSelector(getState());
  const companyID = getCompanyID(userInfo);
  const method = option.method || 'GET';

  return fetch(endpoint, {
    ...option,
    body: JSON.stringify(option.body),
    credentials:
      window.Grab.storage.getItem('__AUTH_HINT__') === 'COOKIE'
        ? 'include'
        : 'omit',
    headers: {
      ...buildHeaders(authType, action, companyID),
      ...option.headers,
    },
  })
    .then(response => {
      if (!response.ok) {
        // Enhanced error context for failed API requests
        const apiError = new Error(
          `API request failed: ${method} ${endpoint} - ${response.status} ${
            response.statusText
          }`,
        );

        // Capture comprehensive error context
        const errorCtx = rumErrorCtx(getDefaultErrorContext())
          .component('fetchHelper')
          .severity(response.status >= 500 ? SEVERITY.HIGH : SEVERITY.MEDIUM)
          .custom('endpoint', endpoint)
          .custom('method', method)
          .custom('statusCode', response.status)
          .custom('statusText', response.statusText)
          .custom('actionType', start ? start().type : 'unknown')
          .build();

        ddRum.addError(apiError, errorCtx);

        return Promise.reject(response);
      }
      if (response.status === 204) return null;
      return response;
    })
    .then(response => (response ? response.json() : response))
    .then(data => {
      if (callback) {
        callback(data);
      }
      const queryStr = new URL(endpoint).search.slice(1);
      const reduxState = getState();
      dispatch(success(data, queryStr, option.body, reduxState));
    })
    .catch(err => {
      // Capture network errors, timeouts, and other fetch failures
      const errorCtx = rumErrorCtx(getDefaultErrorContext())
        .component('fetchHelper')
        .message(`${method} ${endpoint} - ${err.message || 'Unknown error'}`)
        .severity(SEVERITY.HIGH)
        .custom('endpoint', endpoint)
        .custom('method', method)
        .custom('actionType', start ? start().type : 'unknown')
        .build();

      ddRum.addError(err, errorCtx);

      if (
        err &&
        err.status === 401 &&
        start().type !== 'LOGIN_START' &&
        start().type !== 'GET_LKPP_TOKEN_START'
      ) {
        window.Grab.storage.removeItem('__AUTH_HINT__');
        window.location.href = _get(
          window,
          'Grab.config.endpoints.expressHomeBaseUri',
        );
      }
      if (
        err &&
        err.status === 500 &&
        err.url.includes('getServicesAndPools')
      ) {
        const errorCtx = rumErrorCtx(getDefaultErrorContext())
          .component('fetchHelper')
          .severity(SEVERITY.LOW)
          .custom('tags', {
            servicesRequestParams: JSON.stringify(option.body.request),
          })
          .build();
        ddRum.addError(new Error('No services found for city'), errorCtx);
        dispatch(error());
      }
      if (typeof err.text === 'function') {
        err.text().then(text => {
          try {
            const errorData = !isEmpty(text) ? JSON.parse(text) : text;
            dispatch(error(errorData));
          } catch (parseError) {
            dispatch(error());
          }
        });
      } else if (error().type !== 'FETCH_SERVICE_TYPE_ERROR') {
        dispatch(error(err));
      }
    });
};

export default fetchHelper;
